import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Select, Spin, SelectProps, Button, Divider, Space, Tag, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { useCompany } from '../../contexts/CompanyContext';
import { useConfig } from '../../contexts/ConfigContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { useRegister } from '../../contexts/RegisterContext';
import { UnifiedItemModal, StorageItemBasic } from '@ryo/ui';

const { Text } = Typography;

interface ItemSearchSelectProps extends Omit<SelectProps<number>, 'onChange'> {
  value?: number;
  onChange?: (value: number) => void;
  placeholder?: string;
  itemType?: 'ALL' | 'SELL' | 'PURCHASE';
  onItemSelect?: (item: any) => void;
  initialItems?: any[];
  selectedStorehouseId?: number | string;
  allowedRegisterTypes?: ('PRODUCT' | 'SERVICE' | 'FIXED_ASSET')[];
}

interface RenderDropdownProps {
  menu: React.ReactNode;
  onCreateClick: () => void;
}

const RenderDropdown = ({ menu, onCreateClick }: RenderDropdownProps) => (
  <>
    {menu}
    <Divider style={{ margin: '8px 0' }} />
    <Space style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'center' }}>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCreateClick}
        size="small"
        style={{ width: '100%' }}
      >
        Crear
      </Button>
    </Space>
  </>
);

export const ItemSearchSelect: React.FC<ItemSearchSelectProps> = ({
  value,
  onChange,
  placeholder = 'Buscar producto o insumo...',
  itemType = 'ALL',
  onItemSelect,
  initialItems = [],
  selectedStorehouseId,
  allowedRegisterTypes,
  ...rest
}) => {
  const { company } = useCompany();
  const { config } = useConfig();
  const { securePost } = useSecureRequest();
  const { register } = useRegister();

  const rawItemTypes = (register.itemTypes?.length ? register.itemTypes : (company.moduleOptions?.filter((el: any) => el.type === 'ITEM_TYPE') || [])).filter((o: any) => o.show);
  const itemTypeOptions = allowedRegisterTypes && allowedRegisterTypes.length > 0
    ? rawItemTypes.filter((o: any) => allowedRegisterTypes.includes(o.key))
    : rawItemTypes;
  const umOptions = (register.umOptions?.length ? register.umOptions : (company.moduleOptions?.filter((el: any) => el.type === 'UM') || [])).filter((o: any) => o.show);
  const categoryOptions = (register.categoryOptions?.length ? register.categoryOptions : (company.moduleOptions?.filter((el: any) => el.type === 'CATEGORY_OPTION') || [])).filter((o: any) => o.show);

  const [fetching, setFetching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [createdItems, setCreatedItems] = useState<any[]>([]);
  const [showItemModal, setShowItemModal] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const fetchOptions = useCallback((query: string = '') => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!query.trim()) {
      setSearchResults([]);
      setFetching(false);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      if (!company?.current?.uid) return;
      setFetching(true);
      try {
        const filterPayload: any = {
          company: company.current.uid,
          search: query.trim(),
          type: 'full',
          module: config.module,
          hitsPerPage: 25,
        };
        if (itemType === 'SELL') {
          filterPayload.sellItem = true;
          filterPayload.excludeServices = true;
        } else if (itemType === 'PURCHASE') {
          filterPayload.purchaseItem = true;
        }
        if (allowedRegisterTypes?.length === 1 && allowedRegisterTypes[0] === 'PRODUCT') {
          filterPayload.excludeServices = true;
        }

        const response = await securePost('/inventory/item/search-items', filterPayload);
        if (response?.itemList) {
          setSearchResults(response.itemList);
        }
      } catch (error) {
        console.error('Error searching items:', error);
      } finally {
        setFetching(false);
      }
    }, 400);
  }, [company?.current?.uid, config.module, itemType, allowedRegisterTypes, securePost]);

  const currentCompanyUid = company?.current?.uid;

  useEffect(() => {
    setSearchResults([]);
    setCreatedItems([]);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentCompanyUid]);

  const combinedDataMap = new Map();

  // 1. Initial items passed as prop (always available when not actively searching)
  if (searchResults.length === 0) {
    initialItems.forEach(item => {
      if (item?.id !== undefined) combinedDataMap.set(item.id, item);
    });
  } else {
    // 2. Search results from API when user typed a search query
    searchResults.forEach(item => {
      if (item?.id !== undefined) combinedDataMap.set(item.id, item);
    });
  }

  // 3. Newly created items take highest precedence
  createdItems.forEach(item => {
    if (item?.id !== undefined) combinedDataMap.set(item.id, item);
  });

  // Ensure selected value label is available if still not found
  if (value && !combinedDataMap.has(value)) {
    const selectedInitial = initialItems.find(i => i.id === value) || createdItems.find(i => i.id === value);
    if (selectedInitial) {
      combinedDataMap.set(value, selectedInitial);
    }
  }

  const data = Array.from(combinedDataMap.values()).filter(i => {
    if (itemType === 'SELL' && i.register_type === 'SERVICE') return false;
    if (allowedRegisterTypes && allowedRegisterTypes.length > 0) {
      const regType = i.register_type || 'PRODUCT';
      if (!allowedRegisterTypes.includes(regType)) return false;
    }
    // Strictly filter out items from other companies if company_uid is present
    if (currentCompanyUid && i.company_uid && i.company_uid !== currentCompanyUid) return false;
    return true;
  });

  const handleChange = (val: number) => {
    if (onChange) onChange(val);
    if (onItemSelect) {
      const selectedItem = data.find(i => i.id === val);
      onItemSelect(selectedItem);
    }
  };

  // ── Item creation ────────────────────────────────────────────────────────────

  const handleSaveItem = async (values: StorageItemBasic): Promise<any> => {
    if (!company?.current?.uid || !company.current?.id) {
      throw new Error('No se encontró la compañía activa');
    }

    const isService = values.register_type === 'SERVICE';

    // Build prices array
    const prices: any[] = [];
    if (values.sell_price !== undefined && values.sell_price !== null) {
      prices.push({
        id: -1,
        element_id: 0,
        type: 'SELL',
        value: values.sell_price,
        description: 'Precio de venta',
        is_active: true,
        is_default: true,
        is_visible: true,
      });
    }
    if (values.purchase_price !== undefined && values.purchase_price !== null) {
      prices.push({
        id: -1,
        element_id: 0,
        type: 'PURCHASE',
        value: values.purchase_price,
        description: 'Precio de compra',
        is_active: true,
        is_default: true,
        is_visible: true,
      });
    }

    const location = isService ? 'N/A' : undefined;
    const display = isService ? 'N/A' : 'UND';

    const moduleOptions: any[] = [];
    const categoryKey = values.category || 'VRS';
    const displayKey = isService ? 'N/A' : 'UND';

    const itemTypes = register.itemTypes?.length ? register.itemTypes : (company.moduleOptions?.filter((el: any) => el.type === 'ITEM_TYPE') || []);
    const categoryOptionsList = register.categoryOptions?.length ? register.categoryOptions : (company.moduleOptions?.filter((el: any) => el.type === 'CATEGORY_OPTION') || []);
    const displayOptionsList = register.displayOptions?.length ? register.displayOptions : (company.moduleOptions?.filter((el: any) => el.type === 'DISPLAY_OPTION') || []);
    const umOptionsList = register.umOptions?.length ? register.umOptions : (company.moduleOptions?.filter((el: any) => el.type === 'UM') || []);

    const idItemType = itemTypes?.find((el: any) => el.key === values.register_type)?.id;
    const idCategory = categoryOptionsList?.find((el: any) => el.key === categoryKey)?.id;
    const idDisplay = displayOptionsList?.find((el: any) => el.key === displayKey)?.id;
    const idUm = umOptionsList?.find((el: any) => el.key === (isService ? 'UND' : values.measure_unit) || (values.measure_unit === 'UN' && el.key === 'UND'))?.id;

    if (idItemType) {
      moduleOptions.push({
        id: -1,
        option_id: idItemType,
        is_active: true,
        sub_type: 'TYPE_ELEMENT',
        category: 'NATIONAL',
      });
    }
    if (idCategory) {
      moduleOptions.push({
        id: -1,
        option_id: idCategory,
        is_active: true,
        sub_type: 'CATEGORY_ELEMENT',
        category: 'NATIONAL',
      });
    }
    if (idDisplay) {
      moduleOptions.push({
        id: -1,
        option_id: idDisplay,
        is_active: true,
        sub_type: 'DISPLAY_ELEMENT',
        category: 'NATIONAL',
      });
    }
    if (idUm) {
      moduleOptions.push({
        id: -1,
        option_id: idUm,
        is_active: true,
        sub_type: 'UM_ELEMENT',
        category: 'NATIONAL',
      });
    }

    const storageElement: any = {
      company_id: company.current.id,
      company_uid: company.current.uid,
      name: values.name,
      description: values.description,
      is_active: true,
      sell_item: values.sell_item ?? true,
      purchase_item: values.purchase_item ?? false,
      register_type: values.register_type,
      measure_unit: isService ? 'UN' : (values.measure_unit || 'UN'),
      category: values.category || 'VRS',
      display,
      location,
      image: '',
      image_mini: '',
      quantity: 0,
      sell_price: values.sell_price || 0,
      purchase_price: values.purchase_price || 0,
      sell_discount: 0,
      purchase_discount: 0,
      custom_id: values.custom_id || '',
      sku: values.sku || '',
      barcode: '',
      module_options: moduleOptions,
      prices,
      other_values: [],
      inventory: [],
    };

    const res = await securePost('/inventory/item/create-item', {
      storageElement,
      company: company.current.uid,
      uidCompany: company.current.uid,
      module: config.module,
    });

    if (res?.status !== 200) {
      throw new Error(res?.message || 'Error al crear el elemento');
    }

    const createdItem = {
      ...storageElement,
      ...(res.item || {}),
      id: res.item?.id ?? storageElement.id,
      uid: res.item?.uid ?? storageElement.uid,
    };

    return createdItem;
  };

  const handleItemCreateSuccess = (newItem: any) => {
    setShowItemModal(false);
    message.success('Elemento creado exitosamente');

    // Add to created items list so it is always preserved across searches
    setCreatedItems(prev => {
      const filtered = prev.filter(i => i.id !== newItem.id && (!newItem.uid || i.uid !== newItem.uid));
      return [newItem, ...filtered];
    });

    // Also add directly to search results
    setSearchResults(prev => {
      const filtered = prev.filter(i => i.id !== newItem.id && (!newItem.uid || i.uid !== newItem.uid));
      return [newItem, ...filtered];
    });

    // Auto-select the newly created item immediately
    if (newItem.id !== undefined) {
      if (onChange) {
        onChange(newItem.id);
      }
      if (onItemSelect) {
        onItemSelect(newItem);
      }
    }
  };

  const renderDropdown = (menu: React.ReactElement) => (
    <RenderDropdown menu={menu} onCreateClick={() => setShowItemModal(true)} />
  );

  // ────────────────────────────────────────────────────────────────────────────

  const isMobile = config.isMobile || (typeof window !== 'undefined' && window.innerWidth < 768);

  return (
    <>
      <Select
        showSearch
        {...rest}
        value={value}
        allowClear
        placeholder={placeholder}
        notFoundContent={fetching ? <Spin size="small" /> : 'Sin resultados'}
        filterOption={(input, option) => {
          if (!input) return true;
          const item = data.find(i => String(i.id) === String(option?.value));
          if (!item) return false;
          const searchLower = input.toLowerCase();
          const nameMatch = (item.name || '').toLowerCase().includes(searchLower);
          const skuMatch = (item.sku || '').toLowerCase().includes(searchLower);
          const customIdMatch = (item.custom_id || '').toLowerCase().includes(searchLower);
          const barcodeMatch = (item.barcode || '').toLowerCase().includes(searchLower);
          const categoryLabel = categoryOptions.find((c: any) => c.key === item.category)?.string_value || item.category || '';
          const categoryMatch = categoryLabel.toLowerCase().includes(searchLower);
          return nameMatch || skuMatch || customIdMatch || barcodeMatch || categoryMatch;
        }}
        onSearch={fetchOptions}
        onChange={handleChange}
        style={{ width: '100%' }}
        popupMatchSelectWidth={!!isMobile}
        dropdownStyle={
          isMobile
            ? { maxWidth: 'calc(100vw - 24px)', boxSizing: 'border-box' }
            : { minWidth: 320, maxWidth: 580, boxSizing: 'border-box' }
        }
        optionLabelProp="label"
        popupRender={renderDropdown}
      >
        {data.map(item => {
          const isService = item.register_type === 'SERVICE';
          const isFixedAsset = item.register_type === 'FIXED_ASSET';
          const categoryLabel = categoryOptions.find((c: any) => c.key === item.category)?.string_value || item.category;
          const umLabel = umOptions.find((u: any) => u.key === item.measure_unit)?.string_value || item.measure_unit;
          const code = item.custom_id || item.sku;
          const refPrice = item.purchase_price || item.sell_price;
          const cleanLabel = `${item.name}${code ? ` (#${code})` : ''}${item.measure_unit ? ` - ${item.measure_unit}` : ''}`;

          const storeStock = selectedStorehouseId
            ? (item.inventory?.find((inv: any) => String(inv.id) === String(selectedStorehouseId) || String(inv.uid) === String(selectedStorehouseId) || String(inv.storehouse_id) === String(selectedStorehouseId))?.quantity ?? 0)
            : (item.inventory?.reduce((sum: number, inv: any) => sum + (inv.quantity || 0), 0) ?? item.quantity ?? 0);

          return (
            <Select.Option key={item.id} value={item.id} label={cleanLabel} style={{ padding: '6px 8px', height: 'auto', maxWidth: '100%' }}>
              <div style={{ whiteSpace: 'normal', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '100%', overflow: 'hidden' }}>
                {/* Title & Tag */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
                  <Text strong style={{ fontSize: 13, color: '#1f1f1f', lineHeight: '1.3', flex: '1 1 auto', minWidth: 0, wordBreak: 'break-word' }}>
                    {item.name}
                  </Text>
                  <Tag 
                    color={isService ? 'geekblue' : isFixedAsset ? 'purple' : 'green'} 
                    style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 5px', fontWeight: 500, flexShrink: 0 }}
                  >
                    {isService ? 'Servicio' : isFixedAsset ? 'Activo Fijo' : 'Insumo/Prod.'}
                  </Tag>
                </div>

                {/* Metadata row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginTop: 2 }}>
                  {code && (
                    <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#f5f5f5', color: '#595959', padding: '0 4px' }}>
                      Ref: <strong>{code}</strong>
                    </Tag>
                  )}
                  {categoryLabel && categoryLabel !== 'VRS' && (
                    <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#f5f5f5', color: '#595959', padding: '0 4px' }}>
                      Cat: {categoryLabel}
                    </Tag>
                  )}
                  {item.measure_unit && (
                    <Tag bordered={false} style={{ margin: 0, fontSize: 10, background: '#e6f7ff', color: '#096dd9', padding: '0 4px' }}>
                      UM: <strong>{umLabel || item.measure_unit}</strong>
                    </Tag>
                  )}
                  {!isService && (
                    <span style={{ 
                      fontSize: 10, 
                      color: storeStock > 0 ? '#389e0d' : '#cf1322', 
                      background: storeStock > 0 ? '#f6ffed' : '#fff1f0',
                      border: `1px solid ${storeStock > 0 ? '#b7eb8f' : '#ffa39e'}`,
                      borderRadius: 3,
                      padding: '0 4px',
                      fontWeight: 600
                    }}>
                      Stock: {storeStock} {umLabel || item.measure_unit}
                    </span>
                  )}
                  {refPrice !== undefined && refPrice !== null && refPrice > 0 && (
                    <span style={{ fontSize: 10, color: '#389e0d', fontWeight: 600, background: '#f6ffed', padding: '0 4px', borderRadius: 3, border: '1px solid #b7eb8f' }}>
                      Ref: ${Number(refPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>

                {/* Description snippet */}
                {item.description && (
                  <div 
                    style={{ fontSize: 11, color: '#8c8c8c', marginTop: 1, whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} 
                    title={item.description}
                  >
                    {item.description}
                  </div>
                )}
              </div>
            </Select.Option>
          );
        })}
      </Select>

      <UnifiedItemModal
        visible={showItemModal}
        onCancel={() => setShowItemModal(false)}
        onSuccess={handleItemCreateSuccess}
        onSave={handleSaveItem}
        itemTypes={itemTypeOptions as any}
        umOptions={umOptions as any}
        categoryOptions={categoryOptions as any}
      />
    </>
  );
};
